import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Play, Square, Clock, Loader2, ChevronDown, ChevronUp, Briefcase } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import dict from "../../i18n/es-MX";
import { fmtDate as fmtDateShort } from "../../lib/dateFormat";

// ---------- helpers ----------
function toISO(d) { return d.toISOString().slice(0, 10); }
function startOfWeekMonday(d) {
    const nd = new Date(d);
    const day = nd.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    nd.setDate(nd.getDate() + diff);
    nd.setHours(0, 0, 0, 0);
    return nd;
}
function addDays(d, n) { const nd = new Date(d); nd.setDate(nd.getDate() + n); return nd; }
function fmtDuration(mins) {
    if (mins == null) return "—";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m} ${dict.hours.minuteShort}`;
    return `${h} ${dict.hours.hourShort} ${m} ${dict.hours.minuteShort}`;
}
function fmtTimeShort(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
}

function rangeForPreset(preset) {
    const now = new Date();
    if (preset === "today") return { from: toISO(now), to: toISO(now) };
    if (preset === "thisWeek") {
        const ws = startOfWeekMonday(now);
        return { from: toISO(ws), to: toISO(addDays(ws, 6)) };
    }
    if (preset === "thisMonth") {
        return {
            from: toISO(new Date(now.getFullYear(), now.getMonth(), 1)),
            to: toISO(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
        };
    }
    if (preset === "lastMonth") {
        return {
            from: toISO(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
            to: toISO(new Date(now.getFullYear(), now.getMonth(), 0)),
        };
    }
    return null;
}

export default function OperatorHoras() {
    const [preset, setPreset] = useState("thisWeek");
    const [range, setRange] = useState(rangeForPreset("thisWeek"));
    const [sessions, setSessions] = useState([]);
    const [summary, setSummary] = useState({ total_minutes: 0, total_general_minutes: 0, total_by_job: [] });
    const [weekSummary, setWeekSummary] = useState({ total_minutes: 0, total_general_minutes: 0, total_by_job: [] });
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [expanded, setExpanded] = useState(new Set());
    const [tick, setTick] = useState(0);

    // Re-tick every 30s so the "En curso" duration updates
    useEffect(() => {
        const t = setInterval(() => setTick(x => x + 1), 30000);
        return () => clearInterval(t);
    }, []);

    const load = useCallback(async () => {
        setLoading(true); setErr("");
        try {
            const wk = rangeForPreset("thisWeek");
            const [my, sum, weekSum] = await Promise.all([
                api.get("/attendance/my", { params: range }),
                api.get("/attendance/summary", { params: range }),
                api.get("/attendance/summary", { params: wk }),
            ]);
            setSessions(my.data || []);
            setSummary(sum.data || {});
            setWeekSummary(weekSum.data || {});
        } catch (e) {
            setErr(formatApiError(e) || dict.hours.loadError);
        } finally {
            setLoading(false);
        }
    }, [range]);
    useEffect(() => { load(); }, [load]);

    const selectPreset = (p) => {
        setPreset(p);
        const r = rangeForPreset(p);
        if (r) setRange(r);
    };
    const applyCustom = (from, to) => { setPreset("custom"); setRange({ from, to }); };

    const toggleNotes = (id) => {
        setExpanded(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const openSession = useMemo(() => sessions.find(s => !s.session_end_at), [sessions]);
    const openDuration = useMemo(() => {
        if (!openSession) return null;
        const start = new Date(openSession.session_start_at);
        const now = new Date();
        // tick dependency triggers recompute
        void tick;
        return Math.max(0, Math.floor((now - start) / 60000));
    }, [openSession, tick]);

    return (
        <div data-testid="operator-horas" className="px-4 py-5 space-y-5">
            <header>
                <h1 className="font-display text-3xl font-extrabold tracking-tight">{dict.hours.title}</h1>
                <p className="text-xs text-slate-500 mt-1">{dict.hours.subtitle}</p>
            </header>

            {/* Weekly card */}
            <section className="rounded-2xl border border-brand-orange/40 bg-brand-orange/5 p-4 space-y-3" data-testid="horas-week-card">
                <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-wider text-brand-orange font-semibold">{dict.hours.thisWeek}</div>
                    <Clock size={16} className="text-brand-orange"/>
                </div>
                <div className="font-mono text-2xl font-bold text-slate-100" data-testid="horas-week-total">
                    {fmtDuration(weekSummary.total_minutes || 0)}
                </div>
                {(weekSummary.total_by_job?.length || 0) > 0 && (
                    <ul className="text-sm space-y-1" data-testid="horas-week-by-job">
                        {(weekSummary.total_by_job || []).slice(0, 5).map(row => (
                            <li key={row.job_id} className="flex justify-between items-center text-slate-300">
                                <span className="truncate mr-2"><span className="font-mono text-xs text-slate-500">{row.job_code}</span> · {row.client_name || "—"}</span>
                                <span className="font-mono text-slate-100">{fmtDuration(row.minutes)}</span>
                            </li>
                        ))}
                        {(weekSummary.total_general_minutes || 0) > 0 && (
                            <li className="flex justify-between text-slate-400">
                                <span>{dict.hours.general}</span>
                                <span className="font-mono">{fmtDuration(weekSummary.total_general_minutes)}</span>
                            </li>
                        )}
                    </ul>
                )}
            </section>

            {/* Range presets */}
            <div className="flex flex-wrap gap-2" data-testid="horas-range-presets">
                {["today", "thisWeek", "thisMonth", "lastMonth"].map(p => (
                    <button key={p} type="button" onClick={() => selectPreset(p)} data-testid={`horas-range-${p}`}
                        className={`px-3 h-8 rounded-full border text-xs ${preset===p ? "bg-brand-orange text-slate-950 border-brand-orange font-semibold" : "bg-slate-900 text-slate-300 border-slate-700 hover:border-slate-500"}`}>
                        {dict.hours.ranges[p]}
                    </button>
                ))}
                <div className="flex items-center gap-1 ml-1">
                    <input type="date" value={range.from} onChange={e => applyCustom(e.target.value, range.to)} data-testid="horas-custom-from"
                        className="h-8 bg-slate-900 border border-slate-700 rounded-md px-2 text-xs text-slate-100"/>
                    <span className="text-slate-500 text-xs">→</span>
                    <input type="date" value={range.to} onChange={e => applyCustom(range.from, e.target.value)} data-testid="horas-custom-to"
                        className="h-8 bg-slate-900 border border-slate-700 rounded-md px-2 text-xs text-slate-100"/>
                </div>
            </div>

            {/* Summary for range */}
            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 space-y-2" data-testid="horas-range-summary">
                <div className="flex items-baseline justify-between">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.hours.totalWorked}</div>
                    <div className="font-mono text-lg font-bold text-slate-100">{fmtDuration(summary.total_minutes || 0)}</div>
                </div>
                {(summary.total_by_job?.length || 0) > 0 && (
                    <ul className="text-sm space-y-1 border-t border-slate-800 pt-2">
                        {(summary.total_by_job || []).map(row => (
                            <li key={row.job_id} className="flex justify-between items-center text-slate-300" data-testid={`horas-summary-job-${row.job_id}`}>
                                <span className="truncate mr-2"><Briefcase size={11} className="inline text-brand-orange mr-1"/><span className="font-mono text-xs text-slate-500">{row.job_code}</span> · {row.client_name || "—"}</span>
                                <span className="font-mono text-slate-100">{fmtDuration(row.minutes)}</span>
                            </li>
                        ))}
                        {(summary.total_general_minutes || 0) > 0 && (
                            <li className="flex justify-between text-slate-400" data-testid="horas-summary-general">
                                <span>{dict.hours.general}</span>
                                <span className="font-mono">{fmtDuration(summary.total_general_minutes)}</span>
                            </li>
                        )}
                    </ul>
                )}
            </section>

            {/* Sessions list */}
            {loading ? (
                <div className="py-8 text-center"><Loader2 className="animate-spin inline text-slate-500"/></div>
            ) : err ? (
                <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200" data-testid="horas-error">{err}</div>
            ) : sessions.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-800 p-8 text-center text-sm text-slate-500" data-testid="horas-empty">
                    {dict.hours.empty}
                </div>
            ) : (
                <ul className="space-y-3" data-testid="horas-sessions">
                    {sessions.map(s => {
                        const open = !s.session_end_at;
                        const minutes = open ? (s.id === openSession?.id ? openDuration : null) : s.duration_minutes;
                        const hasNotes = !!(s.check_in_note || s.check_out_note);
                        const isExpanded = expanded.has(s.id);
                        return (
                            <li key={s.id} data-testid={`horas-session-${s.id}`}
                                className={`rounded-xl border ${open?"border-brand-orange/60 bg-brand-orange/5":"border-slate-800 bg-slate-900/40"} p-3 space-y-2`}>
                                <div className="flex items-baseline justify-between gap-2">
                                    <div className="text-sm">
                                        <span className="font-medium text-slate-100">{fmtDateShort(s.session_start_at)}</span>
                                        <span className="text-slate-400"> · </span>
                                        <span className="font-mono text-slate-300">
                                            {fmtTimeShort(s.session_start_at)} – {open ? "..." : fmtTimeShort(s.session_end_at)}
                                        </span>
                                    </div>
                                    <span className="font-mono text-sm font-semibold text-slate-100">{fmtDuration(minutes)}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    {s.job_id ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-orange/20 text-brand-orange border border-brand-orange/40">
                                            <Briefcase size={11}/>
                                            <span className="font-mono">{s.job_code}</span>
                                            {s.client_name && <span className="opacity-80">· {s.client_name}</span>}
                                        </span>
                                    ) : (
                                        <span className="inline-flex px-2 py-0.5 rounded-full bg-slate-800 text-slate-400 border border-slate-700">
                                            {dict.hours.general}
                                        </span>
                                    )}
                                    {open && (
                                        <span className="text-[10px] uppercase tracking-wider text-brand-orange font-semibold" data-testid={`horas-session-open-badge-${s.id}`}>{dict.hours.ongoing}</span>
                                    )}
                                </div>
                                {hasNotes && (
                                    <>
                                        <button type="button" onClick={() => toggleNotes(s.id)} data-testid={`horas-session-notes-toggle-${s.id}`}
                                            className="text-[11px] text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">
                                            {isExpanded ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
                                            {isExpanded ? dict.hours.hideNotes : dict.hours.viewNotes}
                                        </button>
                                        {isExpanded && (
                                            <div className="text-[11px] text-slate-300 space-y-1 pt-1 border-t border-slate-800" data-testid={`horas-session-notes-${s.id}`}>
                                                {s.check_in_note && <div><span className="text-slate-500">{dict.hours.checkInNote}: </span>{s.check_in_note}</div>}
                                                {s.check_out_note && <div><span className="text-slate-500">{dict.hours.checkOutNote}: </span>{s.check_out_note}</div>}
                                            </div>
                                        )}
                                    </>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
